/*
  * @prop --component-input-switch-color: The color of the switch
  * @prop --component-input-switch-color-active: The color of the switch when active
  * @prop --component-input-switch-color-disabled: The color of the switch when disabled
  * @prop --component-input-switch-label: The  color of the label for switch
  * @prop --component-input-label: The color of the label
*/

@import './radio-button';
@import './checkbox';

ui-input {
  display: block;

  // Override content-box in Normalize (* isn't specific enough)
  input[type='search'] {
    @include box-sizing(border-box);
  }

  // remove IEs clear X
  input::-ms-clear {
    display: none;
  }

  // reset ios styled input
  input[type='text'],
  input[type='email'],
  input[type='password'],
  input[type='search'],
  input[type='number'],
  input[type='tel'] {
    /* stylelint-disable-next-line plugin/no-unsupported-browser-features */
    appearance: none;
    border-radius: 4px;
    height: $input-height;
    width: 100%;

    &:focus {
      outline: none;
    }
  }

  // remove arrows for number inputs in chrome and Firefox
  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    /* stylelint-disable-next-line plugin/no-unsupported-browser-features */
    appearance: none;
    margin: 0;
  }

  input[type='number'] {
    /* stylelint-disable-next-line plugin/no-unsupported-browser-features */
    appearance: textfield;
  }

  .input {
    font-family: var(--skin-font-family-primary, $font-family-sans-serif);
    font-size: 16px;
    line-height: 24px;
    font-weight: $font-weight-normal;

    &__label {
      font-size: inherit;
      line-height: inherit;
      font-weight: inherit;
      display: inline-block;
      max-width: 100%;
      margin-top: -5px;
      margin-bottom: 5px;
      color: var(--component-input-label, var(--current-color-text, $ui-dark));

      > .required {
        margin-left: 1px;
      }
    }

    &__icon-wrapper {
      position: relative;
    }

    &__field {
      font-size: inherit;
      line-height: inherit;
      font-weight: inherit;
      box-sizing: border-box;
      padding: 0 9px 0 14px;
      color: var(--skin-color-text, $ui-dark);
      background-color: var(--skin-color-white, $ui-white);
      // Reset unusual Firefox-on-Android default style; see https://github.com/necolas/normalize.css/issues/214
      background-image: none;
      border: 1px solid $input-border-color;
      transition: border-color 0.1s linear;
      border-radius: 4px;

      // Placeholder
      @include placeholder;

      &:focus {
        border-color: var(--skin-color-primary, $ui-blue);
        outline: none;
      }

      .input--bg-dark & {
        color: $ui-dark;

        &:focus {
          border-color: $ui-grey-plus-2;
        }

        // Placeholder
        @include placeholder;
      }
    }

    &__icons {
      position: absolute;
      height: 100%;
      top: 0;
      right: 10px;
      display: flex;
      align-items: center;

      > p {
        margin-right: 10px;
        color: var(--skin-color-secondary, $ui-grey);
      }
    }

    &__icon {
      pointer-events: none;
      width: 24px;
      height: 24px;

      &.number-stepper,
      &[icon='close-circle'],
      &[icon='magnifying-glass'],
      &[icon='chevron-down'] {
        pointer-events: all;
        cursor: pointer;
      }

      &.number-stepper.disabled {
        pointer-events: none;

        --component-icon-color: var(--skin-color-secondary, #{$ui-grey});
      }

      &[icon='close-circle'] {
        --component-icon-color: var(--skin-color-secondary, #{$ui-grey});
      }

      + .input__icon {
        margin-left: 16px;
      }
    }

    &__hint {
      font-size: 16px;
      line-height: 24px;
      margin-top: 10px;
    }

    &__message {
      display: block;
      font-size: 15px;
      line-height: 20px;
      font-weight: $font-weight-normal;
      padding-top: 7px;
      text-align: left;
    }

    &--white {
      .input {
        &__label {
          color: var(--skin-color-white, $ui-white);
        }
      }
    }

    &--error {
      .input {
        &__label {
          color: $ui-red;
        }

        &__icon {
          &[icon='warning'] {
            --component-icon-color: #{$ui-red};

            margin-right: 10px;
          }

          --component-icon-color: #{$ui-grey};
        }

        &__field {
          padding-right: 35px;
          border-color: $ui-red;
          color: $ui-red;
        }

        &__message {
          color: $ui-red;
        }
      }
    }

    &--success {
      .input {
        &__label {
          color: $ui-green;
        }

        &__icon {
          --component-icon-color: #{$ui-green};

          margin-right: 10px;
        }

        &__field {
          padding-right: 35px;
          border-color: $ui-green;
          color: $ui-green;
        }

        &__message {
          color: $ui-green;
        }
      }
    }

    &--switch {
      display: flex;
      align-items: center;
      margin-bottom: 5px;

      .input {
        &__label {
          padding-left: 1rem;
          margin-bottom: 0;
          color: var(
            --component-input-switch-label,
            var(--current-color-text, $ui-dark)
          );
        }

        &__switch {
          position: relative;
          display: inline-block;
          width: 32px;
          height: 18px;

          input {
            &:checked + .input__switch__slider {
              border-color: var(
                --component-input-switch-color-active,
                var(--skin-color-accent, $ui-green)
              );

              &::before {
                transform: translateX(14px);
                background-color: var(
                  --component-input-switch-color-active,
                  var(--skin-color-accent, $ui-green)
                );
              }
            }
          }

          &__slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border-radius: 20px;
            background-color: transparent;
            border: 1px solid
              var(
                --component-input-switch-color,
                var(--current-color-text, $ui-dark)
              );
            transition: 0.4s;

            &::before {
              position: absolute;
              content: '';
              height: 12px;
              width: 12px;
              left: 2px;
              bottom: 2px;
              border-radius: 50%;
              background-color: var(
                --component-input-switch-color,
                var(--current-color-text, $ui-dark)
              );
              transition: 0.4s;
            }
          }
        }

        &__field {
          &:focus-visible {
            + .input__switch__slider {
              outline: -webkit-focus-ring-color auto 1px;
            }
          }
        }
      }
    }

    &--disabled {
      .input {
        &__field {
          border-color: var(
            --component-input-switch-color-disabled,
            $ui-grey-plus-1
          );
        }

        &__label {
          color: var(--component-input-switch-color-disabled, $ui-grey-plus-1);
        }

        &__switch {
          input:checked + .input__switch__slider,
          &__slider {
            cursor: not-allowed;
            border-color: var(
              --component-input-switch-color-disabled,
              $ui-grey-plus-1
            );

            &::before {
              background-color: var(
                --component-input-switch-color-disabled,
                $ui-grey-plus-1
              );
            }
          }
        }
      }
    }
  }

  & + .btn {
    margin-top: 15px;
  }

  ui-icon.input__icon.icon-search.icon-active {
    --component-icon-color: var(--skin-color-primary, #{$ui-blue});
  }
}
